import { ReactNode, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { ISingleEventLeaderboard, ISingleEventRankRanges } from 'routes/singleEvent/types';

import { MarkdownContent } from 'components/markdownContent';
import { Pagination } from 'components/pagination';
import Spacing from 'components/spacing/Spacing';

import { parseHighlightedText } from '../../utils/parseHighlightedText';
import { formatRMVWithIcon } from '../../utils/formatRMVWithIcon';

import styles from '../../SingleEvent.module.scss';
import { RankRangesSection } from './RankRangesSection';
import { TableHeaderRow } from './TableHeaderRow';

const ITEMS_PER_PAGE = 5;

const RMV_COLUMNS = new Set(['totalTradingVolumeUSDT', 'estimatedRewardUSDT']);

const renderCellContent = (columnKey: string, value: string | number | boolean | undefined): ReactNode => {
  const stringValue = String(value ?? '-');

  // For RMV columns, render with icon + formatted value
  if (RMV_COLUMNS.has(columnKey) && stringValue !== '-') {
    const formatted = formatRMVWithIcon(stringValue);
    if (formatted !== stringValue) {
      return formatted;
    }
  }

  if (typeof value === 'string') {
    return parseHighlightedText(value);
  }

  return value;
};

type EventArenaSectionProps = {
  leaderboard: ISingleEventLeaderboard;
  rankRanges?: ISingleEventRankRanges;
  rankingRules?: ISingleEventRankRanges;
  variant?: 'trading' | 'world-nft';
};

const EventArenaSection = ({ leaderboard, rankRanges, rankingRules, variant = 'trading' }: EventArenaSectionProps) => {
  // Support both prop names for backwards compatibility
  const ranksData = rankRanges || rankingRules;
  const [currentPage, setCurrentPage] = useState(1);

  const paginationData = useMemo(() => {
    if (!leaderboard?.items) {
      return {
        displayItems: [],
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false,
        isPaginated: false,
      };
    }

    // If pagination is disabled, show all items
    if (!leaderboard.enablePagination) {
      return {
        displayItems: leaderboard.items,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: false,
        isPaginated: false,
      };
    }

    // Pagination enabled
    const totalPages = Math.ceil(leaderboard.items.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const displayItems = leaderboard.items.slice(startIndex, endIndex);

    return {
      displayItems,
      totalPages,
      hasNextPage: currentPage < totalPages,
      hasPreviousPage: currentPage > 1,
      isPaginated: true,
    };
  }, [leaderboard?.items, leaderboard?.enablePagination, currentPage]);

  const goToPage = (page: number) => {
    setCurrentPage(page);
  };

  const goToNextPage = () => {
    if (paginationData.hasNextPage) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const goToPreviousPage = () => {
    if (paginationData.hasPreviousPage) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  if (!leaderboard) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <Spacing mobileHeight={60} />
      <div className="tw-mx-auto tw-max-w-[1280px]">
        <div className="tw-px-[20px] lg:tw-px-0">
          <h2 className={twMerge(styles.sectionHeader, 'tw-mb-[20px] tw-text-[32px] lg:tw-mb-[40px]')}>
            {leaderboard.title}
          </h2>
          {variant === 'world-nft' ? (
            <>
              {leaderboard.subtitle && (
                <h2 className="tw-mb-[20px] tw-text-[22px] tw-font-bold tw-text-white lg:tw-text-[32px]">
                  {parseHighlightedText(leaderboard.subtitle)}
                </h2>
              )}
              <p className="tw-text-[20px] tw-font-medium tw-text-white">{leaderboard.description}</p>
            </>
          ) : (
            <MarkdownContent content={leaderboard.description} className="tw-text-white" />
          )}
        </div>

        <div className="tw-mt-[30px] lg:tw-mt-[60px]">
          <div className="tw-mb-[10px] tw-flex tw-flex-row tw-items-end tw-gap-[16px] tw-px-[16px] tw-text-white lg:tw-items-center">
            {leaderboard.columns.map((column) => (
              <TableHeaderRow key={column.key} title={column.header} />
            ))}
          </div>

          <div className="tw-flex tw-flex-col tw-gap-[6px] lg:tw-gap-[16px]">
            {paginationData.displayItems.map((item, index) => {
              const rowKey = `${item.rank || item.airdrop || index}`;

              if (item.isCurrentUser) {
                return (
                  <div key={rowKey} className="tw-bg-currentStageGradient tw-p-[2px] lg:tw-rounded-[12px]">
                    <div className="tw-flex tw-flex-row tw-items-center tw-gap-[16px] tw-px-[16px] tw-py-[6px] lg:tw-items-start lg:tw-py-[14px]">
                      {leaderboard.columns.map((column) => (
                        <div
                          key={column.key}
                          className="tw-flex-1 tw-whitespace-pre-line tw-text-center tw-text-[14px] tw-font-semibold tw-leading-[24px] tw-text-backgroundColorDark lg:tw-text-[18px]"
                        >
                          {renderCellContent(column.key, item[column.key])}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <div key={rowKey}>
                  <div className="tw-flex tw-flex-row tw-items-center tw-gap-[16px] tw-bg-leaderboardRowGradient tw-px-[16px] tw-py-[6px] lg:tw-items-start lg:tw-py-[14px]">
                    {leaderboard.columns.map((column) => (
                      <div
                        key={column.key}
                        className="tw-flex-1 tw-whitespace-pre-line tw-text-center tw-text-[14px] tw-font-semibold tw-leading-[24px] tw-text-white lg:tw-text-[18px]"
                      >
                        {renderCellContent(column.key, item[column.key])}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {paginationData.isPaginated && paginationData.totalPages > 1 && (
            <>
              <div className="tw-mb-[20px] lg:tw-mb-12" />
              <Pagination
                currentPage={currentPage}
                totalPages={paginationData.totalPages}
                hasNextPage={paginationData.hasNextPage}
                hasPreviousPage={paginationData.hasPreviousPage}
                goToPage={goToPage}
                goToNextPage={goToNextPage}
                goToPreviousPage={goToPreviousPage}
              />
            </>
          )}
          <div className="tw-mt-[30px] lg:tw-mt-[60px]" />
          {leaderboard.lastUpdated && (
            <div className="tw-flex tw-flex-col tw-gap-[8px] tw-px-[20px] tw-text-[18px] tw-font-semibold tw-text-menuLinkColor lg:tw-px-0">
              <p>Data Last Updated {new Date(leaderboard.lastUpdated).toLocaleString()}</p>
              <p>
                Estimated rewards are for reference only. To receive the estimated reward, users must reach the minimum
                requirement for their ranking.
              </p>
            </div>
          )}
          {ranksData && (
            <>
              <div className="tw-mt-[40px] lg:tw-mt-[60px]" />
              <RankRangesSection rankRanges={ranksData} />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default EventArenaSection;
